import SceneObject from "../../../lib/services/scene/sceneobject";
import ShaderService from "../../../lib/services/shader/shader_service";
import PapercraftStaticRenderPipeline from "../../../lib/services/render/pipelines/papercraft_static_renderpipeline";
import Camera from "../../../lib/services/scene/camera";
import ResourceService from "../../../lib/services/resource/resourceservice";

const SPIN_RATE_RAD_SEC = 40.0;
const ARC_LIFT_FACTOR = 0.35;

const FRISBEE_LAND_Y = 0.06;          // rest height above ground for the disc
const FRISBEE_LAND_PITCH = Math.PI * -0.05;         // final pitch at touchdown (radians)
const FLATTEN_START_T = 0.7;          // (already present if you have it)

// Tunables — start here
const ARC_LAUNCH_HEIGHT = 0.9;     // world units; height of P1 above straight line
const ARC_GLIDE_HEIGHT  = 0.6;     // world units; height of P2 above straight line
const ARC_P1_ALONG      = 0.22;    // how soon P1 occurs along the throw
const ARC_P2_ALONG      = 0.78;    // how late P2 occurs along the throw

// Distance-based scaling (optional): blend toward smaller arcs for short throws,
// don't let long throws balloon.
const MIN_ARC_HEIGHT_DIST = 0.8;    // throws this short or less use 0% of arc heights
const FULL_ARC_HEIGHT_DIST = 5.0;   // throws this long or more use 100% of arc heights

const OBJECT_OVER_HEIGHT      = 0.6;   // how far above the cap the disc passes
const OBJECT_PAST_DISTANCE    = 1.8;   // ground landing distance past the cap (XZ)
const OBJECT_ARC_LAUNCH_HEIGHT = 1.0;  // P1 lift above launch line
const OBJECT_ARC_GLIDE_HEIGHT  = 0.5;  // P2 lift relative to PASS height
const OBJECT_ARC_P1_ALONG      = 0.30; // P1 lateral position from P0 toward PASS
const OBJECT_ARC_P2_ALONG      = 0.70; // P2 lateral position from PASS toward P3

export type ThrowTarget =
  | { kind: "ground_point"; point: Float32Array }
  | {
      kind: "object_pass";
      node_id: string;
      cap_top: Float32Array;    // cap node position (top center)
      over_height: number;      // how far above cap to pass
      past_distance: number;    // ground landing distance past cap (XZ)
    };

export type FrisbeeState = "stowed" | "flying" | "landed" | "passed";

export default class Frisbee extends SceneObject {
  static_renderpipeline: PapercraftStaticRenderPipeline;

  private state: FrisbeeState = "stowed";
  private visible_self: boolean = true;

  private p0: Float32Array = new Float32Array(3);
  private p1: Float32Array = new Float32Array(3);
  private p2: Float32Array = new Float32Array(3);
  private p3: Float32Array = new Float32Array(3);

  private elapsed: number = 0;
  private duration: number = 0;
  private pass_t: number = -1;
  private target_kind: "ground_point" | "object_pass" | null = null;
  private landed_pos: Float32Array | null = null;
  private pass_point: Float32Array | null = null;

  private spin_angle: number = 0;

  constructor(key: string) {
    super(key);
    this.static_renderpipeline = new PapercraftStaticRenderPipeline(
      "frisbee", "papercraft_static", "frisbee", "frisbee"
    );
  }

  async build(
    device: GPUDevice, format: GPUTextureFormat,
    shader_service: ShaderService, resource_service: ResourceService
  ): Promise<void> {
    await this.static_renderpipeline.build(device, format, shader_service, resource_service);

     this.transform.set_position(0.5217898488044739,0.046060290187597275,4.737761974334717); //behind the doghouse
    this.transform.set_rotation(0, 0, 0);
    this.transform.set_scale(1, 1, 1);
    this.transform.update_matrix();
  }

  // ---- Public API ---------------------------------------------------------

  throw_to(start: Float32Array, target: ThrowTarget, duration: number): void {
    this.p0 = new Float32Array([start[0], start[1], start[2]]);
    this.duration = duration;
    this.elapsed = 0;
    this.spin_angle = 0;
    this.landed_pos = null;

    if (target.kind === "ground_point") {
      this.target_kind = "ground_point";
      this.pass_point = null;
      this.build_ground_curve(target.point);
      this.pass_t = -1;
    } 
    else
    {
      this.target_kind = "object_pass";
      this.build_object_curve_v2(
         target.cap_top,             // or compute from existing pass_point - over_height
         target.over_height,
         target.past_distance,
      );
      this.pass_t = this.compute_pass_t();
    }

    this.state = "flying";
    this.visible_self = true;

    this.transform.set_position(this.p0[0], this.p0[1], this.p0[2]);
    this.transform.update_matrix();
  }

  stow(): void {
    this.state = "stowed";
    this.landed_pos = null;
  }

  get_state(): FrisbeeState { return this.state; }
  is_flying(): boolean { return this.state === "flying"; }

  get_world_position(): Float32Array {
    return new Float32Array([
      this.transform.position[0],
      this.transform.position[1],
      this.transform.position[2],
    ]);
  }

  get_flight_progress(): number {
    if (this.duration <= 0) return 0;
    return Math.min(1, this.elapsed / this.duration);
  }

  get_flight_elapsed_seconds(): number { return this.elapsed; }
  get_total_duration_seconds(): number { return this.duration; }

  get_pass_point_time_seconds(): number | null {
    if (this.pass_t < 0) return null;
    return this.pass_t * this.duration;
  }

  get_pass_point_position(): Float32Array | null {
    return this.pass_point ? new Float32Array(this.pass_point) : null;
  }

  get_landing_position(): Float32Array | null {
    return this.landed_pos ? new Float32Array(this.landed_pos) : null;
  }

  // ---- Tick ---------------------------------------------------------------

   tick(delta_time: number): void {

    //this.placement.tick(delta_time);
 
    if (this.state === "flying") {
      this.elapsed += delta_time;
      this.spin_angle += SPIN_RATE_RAD_SEC * delta_time;

      const t = this.get_flight_progress();
      const pos = this.sample_cubic(t);

      // Bank only on ground throws — gentle pitch-down near the end.
      let pitch = 0;
            // +0.15 rad nose-up at takeoff, easing to 0 by mid-flight, then nose-down to -0.20 by landing.
      if (t < 0.4) {
         const u = t / 0.4;
         pitch = 0.15 * (1 - u);
      } else if (t > 0.7) {
         const u = (t - 0.7) / 0.3;
         pitch = -0.20 * u;
      }
      

   // 2. Flatten phase: ease pitch toward FRISBEE_LAND_PITCH and lift Y
  //    toward FRISBEE_LAND_Y across the final FLATTEN range.
  let flatten_u = 0;
  if (t >= FLATTEN_START_T) {
    flatten_u = Math.min(1, (t - FLATTEN_START_T) / (1 - FLATTEN_START_T));
    flatten_u = 1 - (1 - flatten_u) * (1 - flatten_u); // ease-out
  }


      const final_pitch = pitch + (FRISBEE_LAND_PITCH - pitch) * flatten_u;
      const final_roll  = 0;
      // Only nudge Y for ground throws; object_pass should fly straight through.
      let final_y = pos[1];
      final_y = pos[1] + FRISBEE_LAND_Y * flatten_u;
     

      this.transform.set_position(pos[0], final_y, pos[2]);
      this.transform.set_rotation(final_pitch, this.spin_angle, final_roll);
      this.transform.update_matrix();

      // 4. End-of-flight: snap rotation flat so it can't clip the ground.
      if (t >= 1) {
         this.landed_pos = new Float32Array([pos[0],final_y, pos[2]]);
         this.transform.set_rotation(FRISBEE_LAND_PITCH, this.spin_angle, 0);
         this.transform.update_matrix();
         this.state = "landed";
      }
       return;
      }

    if (this.state === "landed" || this.state === "passed") {
      // Optional: small settle wobble could go here. For now, hold the pose.
      return;
    }

    // "stowed": position/rotation is driven by the puppy's attachment update.
  }

  prepare(device: GPUDevice, camera: Camera): void {
    if (!this.visible_self) return;
    this.static_renderpipeline.prepare(device, camera, this.transform);
  }

  draw(pass: GPURenderPassEncoder): void {
    if (!this.visible_self) return;
    this.static_renderpipeline.draw(pass);
  }

  // ---- Curve building -----------------------------------------------------

private build_ground_curve(end: Float32Array): void 
{
  this.p3 = new Float32Array([end[0], end[1], end[2]]);

  const horizontal = Math.hypot(end[0] - this.p0[0], end[2] - this.p0[2]);

  // Smooth ramp from 0 → 1 as distance grows.
  const t = Math.max(0, Math.min(1,
    (horizontal - MIN_ARC_HEIGHT_DIST) /
    (FULL_ARC_HEIGHT_DIST - MIN_ARC_HEIGHT_DIST)
  ));
  const scale = t * t * (3 - 2 * t); // smoothstep

  const p1_lift = ARC_LAUNCH_HEIGHT * scale;
  const p2_lift = ARC_GLIDE_HEIGHT  * scale;

  // Lateral interpolation between P0 and P3.
  this.p1 = new Float32Array([
    this.p0[0] + (end[0] - this.p0[0]) * ARC_P1_ALONG,
    this.p0[1] + (end[1] - this.p0[1]) * ARC_P1_ALONG + p1_lift,
    this.p0[2] + (end[2] - this.p0[2]) * ARC_P1_ALONG,
  ]);

  this.p2 = new Float32Array([
    this.p0[0] + (end[0] - this.p0[0]) * ARC_P2_ALONG,
    this.p0[1] + (end[1] - this.p0[1]) * ARC_P2_ALONG + p2_lift,
    this.p0[2] + (end[2] - this.p0[2]) * ARC_P2_ALONG,
  ]);
}

private build_object_curve_v2(
  cap_top: Float32Array,
  over_height: number,
  past_distance: number
): void {
  // Pass point: directly above the cap.
  const pass_x = cap_top[0];
  const pass_y = cap_top[1] + over_height;
  const pass_z = cap_top[2];

  // XZ direction from launch toward pass point.
  const dx = pass_x - this.p0[0];
  const dz = pass_z - this.p0[2];
  const dist = Math.hypot(dx, dz);
  const ux = dist > 1e-5 ? dx / dist : 0;
  const uz = dist > 1e-5 ? dz / dist : 1;

  // P3: ground landing past the cap along the same XZ direction.
  this.p3 = new Float32Array([
    pass_x + ux * past_distance,
    0,
    pass_z + uz * past_distance,
  ]);

  // Optional store of the pass point, used for catch-time queries later.
  this.pass_point = new Float32Array([pass_x, pass_y, pass_z]);

  // Distance scaling, same idea as the ground throw.
  const t = Math.max(0, Math.min(1,
    (dist - MIN_ARC_HEIGHT_DIST) /
    (FULL_ARC_HEIGHT_DIST - MIN_ARC_HEIGHT_DIST)
  ));
  const scale = t * t * (3 - 2 * t);

  const p1_lift = OBJECT_ARC_LAUNCH_HEIGHT * scale;
  const p2_lift = OBJECT_ARC_GLIDE_HEIGHT * scale;

  // P1 sits between P0 and PASS, lifted above the straight P0→PASS line.
  this.p1 = new Float32Array([
    this.p0[0] + (pass_x - this.p0[0]) * OBJECT_ARC_P1_ALONG,
    this.p0[1] + (pass_y - this.p0[1]) * OBJECT_ARC_P1_ALONG + p1_lift,
    this.p0[2] + (pass_z - this.p0[2]) * OBJECT_ARC_P1_ALONG,
  ]);

  // P2 sits between PASS and the ground landing P3, lifted slightly so the
  // descent past the cap is gentle rather than a sharp drop.
  this.p2 = new Float32Array([
    pass_x + (this.p3[0] - pass_x) * OBJECT_ARC_P2_ALONG,
    pass_y + (this.p3[1] - pass_y) * OBJECT_ARC_P2_ALONG + p2_lift,
    pass_z + (this.p3[2] - pass_z) * OBJECT_ARC_P2_ALONG,
  ]);
}

 
  // ---- Bezier sampling and pass-t -----------------------------------------

  private sample_cubic(t: number): Float32Array {
    const u = 1 - t;
    const uu = u * u;
    const uuu = uu * u;
    const tt = t * t;
    const ttt = tt * t;

    const w0 = uuu;
    const w1 = 3 * uu * t;
    const w2 = 3 * u * tt;
    const w3 = ttt;

    return new Float32Array([
      w0 * this.p0[0] + w1 * this.p1[0] + w2 * this.p2[0] + w3 * this.p3[0],
      w0 * this.p0[1] + w1 * this.p1[1] + w2 * this.p2[1] + w3 * this.p3[1],
      w0 * this.p0[2] + w1 * this.p1[2] + w2 * this.p2[2] + w3 * this.p3[2],
    ]);
  }

  private compute_pass_t(): number {
    if (!this.pass_point) return -1;

    const target_x = this.pass_point[0];
    const target_z = this.pass_point[2];

    let best_t = 0;
    let best_d = Infinity;

    // Coarse scan
    for (let i = 0; i <= 60; i++) {
      const t = i / 60;
      const p = this.sample_cubic(t);
      const dx = p[0] - target_x;
      const dz = p[2] - target_z;
      const d = dx * dx + dz * dz;
      if (d < best_d) { best_d = d; best_t = t; }
    }

    // Refine
    const lo = Math.max(0, best_t - 1 / 60);
    const hi = Math.min(1, best_t + 1 / 60);
    for (let i = 0; i <= 20; i++) {
      const t = lo + (hi - lo) * (i / 20);
      const p = this.sample_cubic(t);
      const dx = p[0] - target_x;
      const dz = p[2] - target_z;
      const d = dx * dx + dz * dz;
      if (d < best_d) { best_d = d; best_t = t; }
    }

    return best_t;
  }


   sample_curve(t: number): Float32Array {
         return this.sample_cubic(t);
   }

   place_on_ground(xz_point: Float32Array): void {
      const y = FRISBEE_LAND_Y;
      this.transform.set_position(xz_point[0], y, xz_point[2]);
      this.transform.set_rotation(FRISBEE_LAND_PITCH, this.spin_angle, 0);
      this.transform.update_matrix();
      this.landed_pos = new Float32Array([xz_point[0], y, xz_point[2]]);
      this.state = "landed";
   }

}